import React, { useState, useEffect } from 'react';
import { AgentLayout } from '../components/layout/AgentLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { agentAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Wallet, TrendingUp, DollarSign, AlertTriangle, ArrowUpRight, 
  ArrowDownRight, Clock, CheckCircle, Package, Users, BarChart3,
  RefreshCw, Banknote, XCircle
} from 'lucide-react';

const AgentDashboard = () => {
  const [performance, setPerformance] = useState(null);
  const [floatWallet, setFloatWallet] = useState(null);
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [cashBalance, setCashBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modal states
  const [showFloatModal, setShowFloatModal] = useState(false);
  const [floatAmount, setFloatAmount] = useState('');
  const [floatNotes, setFloatNotes] = useState('');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [perfRes, floatRes, depositsRes, withdrawalsRes, cashRes] = await Promise.all([
        agentAPI.getMyPerformance(),
        agentAPI.getMyFloatWallet(),
        agentAPI.getMyDeposits('pending'),
        agentAPI.getMyWithdrawals(),
        agentAPI.getMyCashBalance()
      ]);
      
      setPerformance(perfRes.data);
      setFloatWallet(floatRes.data.wallet);
      setPendingDeposits(depositsRes.data.deposits || []);
      setPendingWithdrawals(withdrawalsRes.data.withdrawals?.filter(w => w.status === 'pending' || w.status === 'picked_up') || []);
      setCashBalance(cashRes.data.cash_balance);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Error fetching agent dashboard:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const refreshData = () => {
    setRefreshing(true);
    fetchDashboardData();
  };
  
  const handleConfirmDeposit = async (depositId) => {
    setProcessingId(depositId);
    try {
      await agentAPI.confirmDeposit(depositId);
      toast.success('Deposit confirmed! Customer wallet credited.');
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm deposit');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handlePickupWithdrawal = async (withdrawalId) => {
    setProcessingId(withdrawalId);
    try {
      await agentAPI.pickupWithdrawal(withdrawalId);
      toast.success('Withdrawal picked up! Prepare cash for customer.');
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pickup withdrawal');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleMarkPaid = async (withdrawalId) => {
    setProcessingId(withdrawalId);
    try {
      await agentAPI.markWithdrawalPaid(withdrawalId);
      toast.success('Withdrawal completed! Transaction finished.');
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleRequestFloat = async (e) => {
    e.preventDefault();
    if (!floatAmount || parseFloat(floatAmount) <= 0) {
      return toast.error('Enter a valid amount');
    }
    try {
      await agentAPI.requestFloatTopup(parseFloat(floatAmount), floatNotes.trim());
      toast.success('Float top-up request submitted! Admin will review.');
      setShowFloatModal(false);
      setFloatAmount('');
      setFloatNotes('');
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request float');
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-lg">Loading agent dashboard...</div>
        </div>
      </AgentLayout>
    );
  }

  const floatUtilization = floatWallet ? (floatWallet.balance / floatWallet.cap) * 100 : 0;
  const isLowFloat = floatUtilization < 20;

  return (
    <AgentLayout>
      <div className="max-w-7xl mx-auto space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Agent Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage your float, transactions, and earnings</p>
          </div>
          <Button 
            onClick={refreshData}
            disabled={refreshing}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={`${isLowFloat ? 'bg-red-50 border-red-200' : 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white'}`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-sm flex items-center ${isLowFloat ? 'text-red-700' : 'text-white'}`}>
                <Wallet className="w-4 h-4 mr-2" />
                Float Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${isLowFloat ? 'text-red-900' : 'text-white'}`}>
                {floatWallet?.balance?.toFixed(2) || '0.00'} ZAR
              </div>
              <div className={`text-sm mt-2 ${isLowFloat ? 'text-red-600' : 'text-emerald-100'}`}>
                {floatUtilization.toFixed(1)}% of {floatWallet?.cap?.toFixed(0) || '0'} cap
              </div>
              {isLowFloat && (
                <Badge className="mt-2 bg-red-600 text-white">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Low Float
                </Badge>
              )}
              <Button 
                size="sm" 
                variant="outline" 
                className="w-full mt-3"
                onClick={() => setShowFloatModal(true)}
              >
                <Banknote className="w-3 h-3 mr-1" />
                Request Top-Up
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center text-yellow-600">
                <DollarSign className="w-4 h-4 mr-2" />
                Commission
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {performance?.current_balances?.commission?.toFixed(2) || '0.00'} ZAR
              </div>
              <div className="text-sm text-gray-500 mt-2">
                Earned: {performance?.earnings?.commission_earned?.toFixed(2) || '0.00'} (30d)
              </div>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center text-blue-600">
                <Package className="w-4 h-4 mr-2" />
                Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900">
                {(performance?.transactions?.deposits?.count || 0) + (performance?.transactions?.withdrawals?.count || 0)}
              </div>
              <div className="text-sm text-gray-500 mt-2">
                ↓{performance?.transactions?.deposits?.count || 0} deposits 
                ↑{performance?.transactions?.withdrawals?.count || 0} withdrawals
              </div>
            </CardContent>
          </Card>

          <Card className={`${cashBalance?.variance !== 0 ? 'bg-orange-50 border-orange-200' : 'hover:shadow-lg transition-shadow'}`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-sm flex items-center ${cashBalance?.variance !== 0 ? 'text-orange-700' : 'text-purple-600'}`}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Cash Position
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${cashBalance?.variance !== 0 ? 'text-orange-900' : 'text-gray-900'}`}>
                {cashBalance?.expected_balance?.toFixed(2) || '0.00'} ZAR
              </div>
              <div className="text-sm text-gray-500 mt-2">
                Variance: {cashBalance?.variance?.toFixed(2) || '0.00'} ZAR
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="pending" className="relative">
              Pending Actions
              {(pendingDeposits.length + pendingWithdrawals.length) > 0 && (
                <Badge className="ml-2 bg-red-600">{pendingDeposits.length + pendingWithdrawals.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="cash">Cash Management</TabsTrigger>
            <TabsTrigger value="settlements">Settlements</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {/* Pending Deposits */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ArrowDownRight className="w-5 h-5 mr-2 text-green-600" />
                  Pending Deposits ({pendingDeposits.length})
                </CardTitle>
                <CardDescription>Customer deposits waiting for confirmation</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingDeposits.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No pending deposits</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingDeposits.map((deposit) => (
                      <div key={deposit.deposit_id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-gray-100">{deposit.amount} {deposit.currency}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Customer: {deposit.customer_email}
                          </div>
                          <div className="text-sm text-gray-500">
                            Method: {deposit.method} • {new Date(deposit.created_at).toLocaleString()}
                          </div>
                          {deposit.note && (
                            <div className="text-sm text-gray-500 italic mt-1">Note: {deposit.note}</div>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleConfirmDeposit(deposit.deposit_id)}
                          disabled={processingId === deposit.deposit_id}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {processingId === deposit.deposit_id ? 'Processing...' : 'Confirm Deposit'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Withdrawals */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ArrowUpRight className="w-5 h-5 mr-2 text-orange-600" />
                  Available Withdrawals ({pendingWithdrawals.length})
                </CardTitle>
                <CardDescription>Withdrawal requests you can pick up</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingWithdrawals.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>No available withdrawals</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingWithdrawals.map((withdrawal) => (
                      <div key={withdrawal.withdrawal_id} className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 dark:text-gray-100">{withdrawal.amount} {withdrawal.currency}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Customer: {withdrawal.customer_name || withdrawal.customer_email}
                          </div>
                          <div className="text-sm text-gray-500">
                            Method: {withdrawal.method} • Commission: {withdrawal.commission_amount} {withdrawal.currency}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(withdrawal.created_at).toLocaleString()}
                          </div>
                          {withdrawal.note && (
                            <div className="text-sm text-gray-500 italic mt-1">Note: {withdrawal.note}</div>
                          )}
                          {withdrawal.status === 'picked_up' && (
                            <Badge className="mt-2 bg-blue-100 text-blue-800">Picked Up - Ready to Pay</Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {withdrawal.status === 'pending' ? (
                            <Button 
                              size="sm"
                              onClick={() => handlePickupWithdrawal(withdrawal.withdrawal_id)}
                              disabled={processingId === withdrawal.withdrawal_id}
                              className="bg-orange-600 hover:bg-orange-700"
                            >
                              {processingId === withdrawal.withdrawal_id ? 'Processing...' : 'Pick Up'}
                            </Button>
                          ) : withdrawal.status === 'picked_up' ? (
                            <Button 
                              size="sm"
                              onClick={() => handleMarkPaid(withdrawal.withdrawal_id)}
                              disabled={processingId === withdrawal.withdrawal_id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {processingId === withdrawal.withdrawal_id ? 'Processing...' : 'Mark as Paid'}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-indigo-600" />
                  30-Day Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-2">Deposits</div>
                      <div className="text-3xl font-bold text-green-600">
                        {performance?.transactions?.deposits?.value?.toFixed(2) || '0.00'} ZAR
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {performance?.transactions?.deposits?.count || 0} transactions • 
                        Avg: {performance?.transactions?.deposits?.average?.toFixed(2) || '0.00'} ZAR
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-2">Withdrawals</div>
                      <div className="text-3xl font-bold text-orange-600">
                        {performance?.transactions?.withdrawals?.value?.toFixed(2) || '0.00'} ZAR
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {performance?.transactions?.withdrawals?.count || 0} transactions • 
                        Avg: {performance?.transactions?.withdrawals?.average?.toFixed(2) || '0.00'} ZAR
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-2">Commission Earned</div>
                      <div className="text-3xl font-bold text-yellow-600">
                        {performance?.earnings?.commission_earned?.toFixed(2) || '0.00'} ZAR
                      </div>
                      <div className="text-sm text-gray-500 mt-1 space-y-1">
                        <div>From Deposits: {performance?.earnings?.commission_breakdown?.deposits?.toFixed(2) || '0.00'}</div>
                        <div>From Withdrawals: {performance?.earnings?.commission_breakdown?.withdrawals?.toFixed(2) || '0.00'}</div>
                        <div>Bonuses: {performance?.earnings?.commission_breakdown?.bonuses?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-2">Settlements</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {performance?.settlements?.count || 0} total
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Paid: {performance?.settlements?.paid || 0} • 
                        Pending: {performance?.settlements?.pending || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cash">
            <Card>
              <CardHeader>
                <CardTitle>Cash Balance Management</CardTitle>
                <CardDescription>Track your physical cash position</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-sm text-green-700 mb-1">Cash Received</div>
                      <div className="text-2xl font-bold text-green-900">
                        {cashBalance?.cash_received?.toFixed(2) || '0.00'} ZAR
                      </div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <div className="text-sm text-orange-700 mb-1">Cash Paid</div>
                      <div className="text-2xl font-bold text-orange-900">
                        {cashBalance?.cash_paid?.toFixed(2) || '0.00'} ZAR
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${cashBalance?.variance !== 0 ? 'bg-red-50' : 'bg-blue-50'}`}>
                      <div className={`text-sm mb-1 ${cashBalance?.variance !== 0 ? 'text-red-700' : 'text-blue-700'}`}>
                        Expected Balance
                      </div>
                      <div className={`text-2xl font-bold ${cashBalance?.variance !== 0 ? 'text-red-900' : 'text-blue-900'}`}>
                        {cashBalance?.expected_balance?.toFixed(2) || '0.00'} ZAR
                      </div>
                    </div>
                  </div>

                  {cashBalance?.variance !== 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center mb-2">
                        <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                        <span className="font-semibold text-red-900">Cash Variance Detected</span>
                      </div>
                      <div className="text-red-700">
                        Your actual cash balance differs from expected by {Math.abs(cashBalance?.variance || 0).toFixed(2)} ZAR
                      </div>
                      <Button className="mt-3 bg-red-600 hover:bg-red-700" size="sm">
                        Reconcile Cash Balance
                      </Button>
                    </div>
                  )}

                  <Button variant="outline" className="w-full">
                    View Cash History
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settlements">
            <Card>
              <CardHeader>
                <CardTitle>Settlement Management</CardTitle>
                <CardDescription>Calculate and request settlements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Settlement Period</h3>
                  <p className="text-gray-600 mb-6">
                    Calculate your settlement for any period to see what you owe or are owed
                  </p>
                  <Button className="bg-indigo-600 hover:bg-indigo-700">
                    Calculate Settlement
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Float Request Modal */}
      {showFloatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Banknote className="w-5 h-5 mr-2 text-emerald-600" />
                  Request Float Top-Up
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => {
                    setShowFloatModal(false);
                    setFloatAmount('');
                    setFloatNotes('');
                  }}
                >
                  <XCircle className="w-5 h-5" />
                </Button>
              </CardTitle>
              <CardDescription>
                Request additional float from admin. Current balance: {floatWallet?.balance?.toFixed(2)} ZAR
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRequestFloat} className="space-y-4">
                <div>
                  <Label htmlFor="float-amount">Amount (ZAR)</Label>
                  <Input
                    id="float-amount"
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="0.00"
                    value={floatAmount}
                    onChange={(e) => setFloatAmount(e.target.value)}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cap: {floatWallet?.cap?.toFixed(0)} ZAR • Available: {((floatWallet?.cap || 0) - (floatWallet?.balance || 0)).toFixed(2)} ZAR
                  </p>
                </div>
                <div>
                  <Label htmlFor="float-notes">Reason (Optional)</Label>
                  <Textarea
                    id="float-notes"
                    rows={3}
                    placeholder="Why do you need this top-up?"
                    value={floatNotes}
                    onChange={(e) => setFloatNotes(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">Submit Request</Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowFloatModal(false);
                      setFloatAmount('');
                      setFloatNotes('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </AgentLayout>
  );
};

export default AgentDashboard;
